import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { easing } from "../../lib/animations";
import { useCart } from "../../context/CartContext";
import { PRODUCTS } from "../../data/products";
import { JOURNAL } from "../../data/journal";

const Icon = ({ d, size = 18 }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 22 22"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        aria-hidden="true"
    >
        {d}
    </svg>
);

const CartIcon = () => (
    <Icon
        d={
            <>
                <path d="M3 5h2.5l2 11.5h11" fill="none" />
                <path d="M6 8h13l-1.5 7H7.5" fill="none" />
            </>
        }
    />
);
const AccountIcon = () => (
    <Icon
        d={
            <>
                <circle cx="11" cy="8" r="3.6" fill="none" />
                <path d="M3.5 19c1.3-3.5 4.5-5 7.5-5s6.2 1.5 7.5 5" fill="none" />
            </>
        }
    />
);
const SearchIcon = () => (
    <Icon
        d={
            <>
                <circle cx="10" cy="10" r="6.5" fill="none" />
                <line x1="15" y1="15" x2="19" y2="19" />
            </>
        }
    />
);

/**
 * SearchOverlay — fullscreen brutalist search that lives at App-root.
 * Filters across products + journal posts client-side.
 */
const SearchOverlay = ({ open, onClose }) => {
    const [q, setQ] = useState("");
    const navigate = useNavigate();

    useEffect(() => {
        if (open) {
            setQ("");
            document.body.classList.add("menu-open");
        } else {
            document.body.classList.remove("menu-open");
        }
        return () => document.body.classList.remove("menu-open");
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    const query = q.trim().toLowerCase();
    const productMatches = query
        ? PRODUCTS.filter(
              (p) =>
                  p.name.toLowerCase().includes(query) ||
                  p.materials.toLowerCase().includes(query) ||
                  p.category.includes(query) ||
                  p.n.includes(query),
          ).slice(0, 6)
        : [];
    const journalMatches = query
        ? JOURNAL.filter(
              (j) =>
                  j.title.toLowerCase().includes(query) ||
                  j.excerpt.toLowerCase().includes(query) ||
                  j.tags.some((t) => t.includes(query)),
          ).slice(0, 4)
        : [];

    const onResultClick = (path) => {
        navigate(path);
        onClose();
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    key="search-overlay"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3, ease: easing.primary }}
                    data-testid="search-overlay"
                    style={{
                        position: "fixed",
                        inset: 0,
                        zIndex: 350,
                        background: "var(--ink)",
                        color: "var(--on-ink)",
                        display: "flex",
                        flexDirection: "column",
                        padding: "clamp(20px, 5vw, 36px)",
                    }}
                >
                    {/* Header bar */}
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 18,
                            paddingBottom: 18,
                            borderBottom: "1px solid var(--on-ink-line)",
                        }}
                    >
                        <span
                            style={{
                                fontFamily: "var(--font-mono)",
                                fontSize: 11,
                                letterSpacing: "0.28em",
                                textTransform: "uppercase",
                                color: "var(--on-ink-mute)",
                            }}
                        >
                            Search the field
                        </span>
                        <button
                            type="button"
                            onClick={onClose}
                            data-testid="search-close"
                            aria-label="Close search"
                            style={{
                                background: "transparent",
                                border: "1px solid var(--on-ink-line-strong, rgba(255,255,255,0.45))",
                                color: "var(--on-ink)",
                                width: 36,
                                height: 36,
                                cursor: "pointer",
                                padding: 0,
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                                <line x1="2" y1="2" x2="12" y2="12" stroke="currentColor" />
                                <line x1="12" y1="2" x2="2" y2="12" stroke="currentColor" />
                            </svg>
                        </button>
                    </div>

                    {/* Input */}
                    <input
                        type="text"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="cotton, hood, field 002…"
                        autoFocus
                        data-testid="search-input"
                        style={{
                            background: "transparent",
                            border: "none",
                            borderBottom: "1px solid var(--on-ink-line)",
                            color: "var(--on-ink)",
                            fontFamily: "var(--font-display)",
                            fontWeight: 500,
                            fontSize: "clamp(28px, 5vw, 56px)",
                            letterSpacing: "-0.025em",
                            padding: "clamp(28px, 4vw, 48px) 0",
                            outline: "none",
                            textTransform: "uppercase",
                        }}
                    />

                    {/* Results */}
                    <div
                        style={{
                            flex: 1,
                            overflowY: "auto",
                            paddingTop: 20,
                            display: "flex",
                            flexDirection: "column",
                            gap: 28,
                        }}
                        data-testid="search-results"
                    >
                        {!query && (
                            <div
                                style={{
                                    paddingTop: 20,
                                    fontFamily: "var(--font-serif)",
                                    fontStyle: "italic",
                                    fontSize: "clamp(15px, 1.4vw, 19px)",
                                    color: "var(--on-ink-mute)",
                                    maxWidth: "48ch",
                                }}
                            >
                                Type a material, drop or artifact name.
                                Esc closes — instinct says yes.
                            </div>
                        )}

                        {query && productMatches.length === 0 && journalMatches.length === 0 && (
                            <div
                                data-testid="search-empty"
                                style={{
                                    paddingTop: 20,
                                    fontFamily: "var(--font-mono)",
                                    fontSize: 12,
                                    letterSpacing: "0.24em",
                                    textTransform: "uppercase",
                                    color: "var(--on-ink-mute)",
                                }}
                            >
                                Nothing in the field — try another cut.
                            </div>
                        )}

                        {productMatches.length > 0 && (
                            <div data-testid="search-products">
                                <span
                                    style={{
                                        display: "block",
                                        marginBottom: 12,
                                        fontFamily: "var(--font-mono)",
                                        fontSize: 10,
                                        letterSpacing: "0.28em",
                                        textTransform: "uppercase",
                                        color: "var(--on-ink-mute)",
                                    }}
                                >
                                    [ Artifacts · {productMatches.length} ]
                                </span>
                                {productMatches.map((p) => (
                                    <button
                                        type="button"
                                        key={p.n}
                                        onClick={() => onResultClick(`/product/${p.slug}`)}
                                        data-testid={`search-result-product-${p.slug}`}
                                        style={{
                                            display: "flex",
                                            gap: 14,
                                            alignItems: "center",
                                            background: "transparent",
                                            border: "none",
                                            color: "var(--on-ink)",
                                            padding: "12px 0",
                                            borderTop: "1px solid var(--on-ink-line)",
                                            width: "100%",
                                            textAlign: "left",
                                            cursor: "pointer",
                                        }}
                                    >
                                        <span
                                            style={{
                                                width: 46,
                                                height: 46,
                                                background: p.bgTone,
                                                display: "inline-flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                flexShrink: 0,
                                            }}
                                        >
                                            <img
                                                src={p.cover}
                                                alt=""
                                                style={{
                                                    width: "80%",
                                                    height: "80%",
                                                    objectFit: "contain",
                                                    mixBlendMode: "multiply",
                                                }}
                                            />
                                        </span>
                                        <span
                                            style={{
                                                flex: 1,
                                                fontFamily: "var(--font-display)",
                                                fontSize: "clamp(15px, 1.3vw, 18px)",
                                                fontWeight: 500,
                                                letterSpacing: "0.04em",
                                                textTransform: "uppercase",
                                            }}
                                        >
                                            {p.name}
                                        </span>
                                        <span
                                            style={{
                                                fontFamily: "var(--font-mono)",
                                                fontSize: 11,
                                                letterSpacing: "0.18em",
                                                color: "var(--on-ink-mute)",
                                            }}
                                        >
                                            € {p.priceEUR}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        )}

                        {journalMatches.length > 0 && (
                            <div data-testid="search-journal">
                                <span
                                    style={{
                                        display: "block",
                                        marginBottom: 12,
                                        fontFamily: "var(--font-mono)",
                                        fontSize: 10,
                                        letterSpacing: "0.28em",
                                        textTransform: "uppercase",
                                        color: "var(--on-ink-mute)",
                                    }}
                                >
                                    [ Journal · {journalMatches.length} ]
                                </span>
                                {journalMatches.map((j) => (
                                    <button
                                        type="button"
                                        key={j.slug}
                                        onClick={() => onResultClick(`/journal/${j.slug}`)}
                                        data-testid={`search-result-journal-${j.slug}`}
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 6,
                                            background: "transparent",
                                            border: "none",
                                            color: "var(--on-ink)",
                                            padding: "12px 0",
                                            borderTop: "1px solid var(--on-ink-line)",
                                            width: "100%",
                                            textAlign: "left",
                                            cursor: "pointer",
                                        }}
                                    >
                                        <span
                                            style={{
                                                fontFamily: "var(--font-display)",
                                                fontSize: "clamp(15px, 1.3vw, 18px)",
                                                fontWeight: 500,
                                                letterSpacing: "0.04em",
                                                textTransform: "uppercase",
                                            }}
                                        >
                                            {j.title}
                                        </span>
                                        <span
                                            style={{
                                                fontFamily: "var(--font-serif)",
                                                fontStyle: "italic",
                                                fontSize: 13,
                                                color: "var(--on-ink-mute)",
                                            }}
                                        >
                                            {j.excerpt.slice(0, 90)}…
                                        </span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

/**
 * MobileBottomBar — sticky bottom tab bar for mobile (≤768px) only.
 * Rotating P sygnet → home / top
 * Cart / Account / Search shortcuts.
 * Lives at App-root, outside Header's mix-blend-mode tree.
 */
export const MobileBottomBar = () => {
    const { count } = useCart();
    const [searchOpen, setSearchOpen] = useState(false);
    const navigate = useNavigate();
    const videoRef = useRef(null);

    // Kick the sygnet video to play (mirrors Header logic — autoplay may be
    // blocked even with `muted`, so we explicitly play on mount + slow it down
    // to match the brand's quiet-luxury cadence).
    useEffect(() => {
        const v = videoRef.current;
        if (!v) return;
        let cancelled = false;
        const start = () => {
            if (cancelled) return;
            try {
                v.playbackRate = 0.25;
            } catch (e) {
                /* ignore */
            }
            const p = v.play();
            if (p && typeof p.catch === "function") p.catch(() => {});
        };
        if (v.readyState >= 1) {
            start();
        } else {
            v.addEventListener("loadedmetadata", start, { once: true });
        }
        return () => {
            cancelled = true;
            v.removeEventListener("loadedmetadata", start);
        };
    }, []);

    const goHome = (e) => {
        e.preventDefault();
        if (window.location.pathname === "/") {
            window.scrollTo({ top: 0, behavior: "smooth" });
        } else {
            navigate("/");
        }
    };

    return (
        <>
            <div
                data-testid="mobile-bottom-bar"
                className="proof-bottom-bar"
                style={{
                    position: "fixed",
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 90,
                    background: "rgba(12, 12, 12, 0.92)",
                    backdropFilter: "blur(14px) saturate(140%)",
                    WebkitBackdropFilter: "blur(14px) saturate(140%)",
                    borderTop: "1px solid var(--on-ink-line)",
                    display: "none",
                    gridTemplateColumns: "repeat(4, 1fr)",
                    padding: "10px clamp(14px, 4vw, 24px) calc(env(safe-area-inset-bottom, 0px) + 10px)",
                    color: "var(--on-ink)",
                }}
            >
                {/* Animated PROOF sygnet video → home */}
                <Link
                    to="/"
                    onClick={goHome}
                    data-testid="bottom-bar-home"
                    aria-label="Home"
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "inherit",
                        textDecoration: "none",
                    }}
                >
                    <video
                        ref={videoRef}
                        autoPlay
                        muted
                        playsInline
                        loop
                        preload="auto"
                        poster="/assets/yk/sygnet-white.png"
                        data-testid="bottom-bar-home-video"
                        style={{
                            width: 30,
                            height: 30,
                            display: "block",
                            objectFit: "contain",
                            userSelect: "none",
                            pointerEvents: "none",
                            background: "transparent",
                        }}
                    >
                        <source src="/assets/yk/proof-nav-video.mp4" type="video/mp4" />
                    </video>
                </Link>

                {/* Cart */}
                <Link
                    to="/cart"
                    data-testid="bottom-bar-cart"
                    aria-label="Cart"
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "inherit",
                        textDecoration: "none",
                        position: "relative",
                    }}
                >
                    <CartIcon />
                    {count > 0 && (
                        <span
                            data-testid="bottom-bar-cart-count"
                            style={{
                                position: "absolute",
                                top: -4,
                                right: -2,
                                minWidth: 16,
                                height: 16,
                                padding: "0 5px",
                                background: "var(--signal)",
                                color: "#fff",
                                fontFamily: "var(--font-mono)",
                                fontSize: 9,
                                fontWeight: 600,
                                lineHeight: 1,
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            {count}
                        </span>
                    )}
                </Link>

                {/* Account */}
                <Link
                    to="/account"
                    data-testid="bottom-bar-account"
                    aria-label="Account"
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "inherit",
                        textDecoration: "none",
                    }}
                >
                    <AccountIcon />
                </Link>

                {/* Search */}
                <button
                    type="button"
                    onClick={() => setSearchOpen(true)}
                    data-testid="bottom-bar-search"
                    aria-label="Search"
                    style={{
                        background: "transparent",
                        border: "none",
                        color: "inherit",
                        cursor: "pointer",
                        padding: 0,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <SearchIcon />
                </button>
            </div>

            <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
        </>
    );
};

export default MobileBottomBar;
